import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useEmailChangeTokenValidation } from "../../hooks/useEmailChangeTokenValidation";

import FormWrapper from "./FormWrapper";
import AlertGreen from "./AlertGreen";
import AlertRed from "../Shared/AlertRed";

const EmailChangeVerification = () => {
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  const token = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("token") || null;
  }, []);

  const { result, isLoading } = useEmailChangeTokenValidation(token);

  // Redirect countdown timer
  useEffect(() => {
    if (redirectCountdown === null) return;
    if (redirectCountdown === 0) {
      navigate("/settings?emailChanged=true");
      return;
    }
    const timer = setTimeout(() => setRedirectCountdown(redirectCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [redirectCountdown, navigate]);

  // Handle validation result
  useEffect(() => {
    if (result) {
      if (result.success) {
        setRedirectCountdown(3);
      } else {
        setAlert(
          <AlertRed
            title="Email Change Verification Failed"
            message={
              <div className="space-y-2">
                <p>
                  We couldn't verify your email change. This could be because:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                  <li>
                    The verification link has expired (links expire after 15
                    minutes)
                  </li>
                  <li>The link has already been used</li>
                  <li>The link is invalid or corrupted</li>
                </ul>
                <p className="mt-3">
                  Please go to your{" "}
                  <button
                    onClick={() => navigate("/settings")}
                    className="text-signal-red-700 underline hover:text-signal-red-700"
                  >
                    account settings
                  </button>{" "}
                  to request a new email change verification.
                </p>
              </div>
            }
          />
        );
      }
    }
  }, [result, navigate]);

  // Handle no token case
  useEffect(() => {
    if (!token) {
      setAlert(
        <AlertRed
          title="Invalid Verification Link"
          message={
            <div className="space-y-2">
              <p>No verification token was found in this link.</p>
              <p>
                Please check your email for the correct verification link, or go
                to your{" "}
                <button
                  onClick={() => navigate("/settings")}
                  className="text-signal-red-700 underline hover:text-signal-red-700"
                >
                  account settings
                </button>{" "}
                to request a new verification email.
              </p>
            </div>
          }
        />
      );
    }
  }, [token, navigate]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center py-8">
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-500 mr-3"></div>
            <div>
              <p className="text-lg font-medium text-ink-700">
                Confirming your email change...
              </p>
              <p className="text-sm text-ink-500 mt-1">
                This will only take a moment
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (result?.success) {
      return (
        <AlertGreen
          title="Email Address Successfully Changed!"
          message={
            <>
              Your email address has been successfully updated to{" "}
              <strong>{result.email}</strong>. Redirecting to your account
              settings in {redirectCountdown}...
            </>
          }
        />
      );
    }

    return alert;
  };

  return (
    <div>
      <FormWrapper>
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-ink-900">
              Email Address Change
            </h1>
            <p className="text-sm text-ink-600 mt-2">
              Confirming your new email address
            </p>
          </div>
          {renderContent()}
        </div>
      </FormWrapper>
    </div>
  );
};

export default EmailChangeVerification;
